import { motion } from 'framer-motion';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactSwitch from 'react-switch';
import {
  getAllProfessionals,
  getAllSkillCategories,
  hideMessage,
  selectDaysMDB,
} from '../../../actions';
import CustomPortal from '../../../commonComponents/CustomPortal';
import MobileAppModal from '../../../commonComponents/MobileAppModal';
import '../../../themes/global.scss';
import '../../../themes/home.scss';

import ShowStartTime from '../../../commonComponents/showStartTime';
import userIcon from '../../../images/Fill_1.png';
import arrowIcon from '../../../images/Right_arrow.png';
import autoFillIcon from '../../../images/autofill-icon.svg';
import breakIcon from '../../../images/break-icon.svg';
import calendarIcon from '../../../images/calendar-icon.svg';
import endClockIcon from '../../../images/end-clock.svg';
import houseIcon from '../../../images/house-icon.svg';
import briefcaseIcon from '../../../images/briefcase.svg';
import CloseIcon from '../../../images/icons/CloseIcon';
import WarningIcon from '../../../images/icons/Warning';
import rateIcon from '../../../images/rate-icon.svg';
import startClockIcon from '../../../images/start-clock.svg';
import ModalRatePopup from '../ModalRatePopup';
import ProfessionPopup from '../temp/ProfessionPopup';

import { getAutoFillInfo } from '../../../constants';
import { Colors } from '../../../themes/colors';
import Tooltip from '../../Tooltip';
import ConfirmPopup from '../../shared/ConfirmPopup';
import {
  arrivalTooltipDescription,
  autoFillTooltipDescription,
  departureTooltipDescription,
  workerClassificationTooltipDescription,
} from '../constants';
import SkillsPopup from '../temp/SkillsPopup';
import { getProfessionCode, handleConfirmRemoveDate } from './utils';

const opacityVariants = {
  show: { opacity: 1, display: 'flex' },
  hide: { opacity: 0, transitionEnd: { display: 'none' } },
};

const CalendarField = () => {
  const dispatch = useDispatch();
  const jobDates = useSelector((state) => state.multiday.jobDates);
  const values = !jobDates
    ? []
    : jobDates.map((date) => moment(date).format('ddd, MMM DD, YYYY'));
  const [showWarning, setShowWarning] = useState(false);
  const [showDeleted, setShowDeleted] = useState({ open: false, date: null });

  const handleRemoveDate = (index) => {
    if (jobDates.length <= 2) {
      setShowWarning(true);
    } else {
      setShowDeleted({ open: true, date: jobDates[index] });
    }
  };

  const confirmRemoveDate = () => {
    const jobData = handleConfirmRemoveDate(jobDates, showDeleted.date);
    dispatch(selectDaysMDB({ jobData }));
    setShowDeleted({ open: false, date: null });
  };

  return (
    <>
      <div
        className="row_container"
        style={{
          borderBottom: `1px solid ${Colors.neutral_100}`,
          width: '50%',
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
            }}
          >
            <img
              alt="calendarIcon"
              src={calendarIcon}
              style={{ width: 24, height: 24, marginRight: 8 }}
            />
            <p className="date">Dates</p>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              paddingTop: 20,
              paddingBottom: 20,
              gap: 15,
            }}
          >
            {values.map((date, index) => (
              <div key={date} style={{ display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    fontFamily: 'Nunito',
                    color: Colors.neutral_600,
                    textTransform: 'capitalize',
                  }}
                >
                  {date}
                </span>
                <div
                  onClick={() => handleRemoveDate(index)}
                  style={{
                    marginLeft: 30,
                    marginRight: -30,
                    cursor: 'pointer',
                  }}
                  data-cy="remove-date"
                >
                  <CloseIcon color={Colors.secondary_500} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {showWarning && (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon width={80} height={80} color={Colors.warning_500} />
            </div>
          )}
          title="Sorry!"
          description={
            <>
              This shift cannot be deleted.
              <br />
              You can’t post a multi-day request with less than two shifts.
            </>
          }
          descriptionStyle={{ width: '100%', marginBottom: 80 }}
          rightButtonText="Ok"
          rightButtonAction={() => setShowWarning(false)}
          closePopup={() => setShowWarning(false)}
        />
      )}

      {showDeleted.open && (
        <ConfirmPopup
          title="Remove Date"
          description={
            <>
              Are you sure you would like to remove{' '}
              <strong>
                {moment(showDeleted.date).format('ddd, MMM DD, YYYY')}
              </strong>
              <br />
              from your request?
            </>
          }
          descriptionStyle={{ width: '95%', marginBottom: 50 }}
          leftButtonText="Go Back"
          leftButtonStyle={{
            backgroundColor: Colors.secondary_500,
          }}
          closePopup={() => setShowDeleted({ open: false, date: null })}
          leftButtonAction={() => setShowDeleted({ open: false, date: null })}
          rightButtonText="Remove"
          rightButtonAction={confirmRemoveDate}
        />
      )}
    </>
  );
};

export default ({ job, formValues, setFormValues }) => {
  const user = useSelector((state) => state.user.selectedChildOffice);
  const parentUser = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const {
    lunch,
    professionType,
    skills,
    enableAutoFill,
    rate,
    isLunchPaid,
    offerEndTime,
    offerStartTime,
  } = formValues;
  const [content, setContent] = useState('');
  const [showRatePopup, setShowRatePopup] = useState(false);
  const [showStartTime, setShowStartTime] = useState(false);
  const [modalIsOpen, setModalOpen] = useState(false);
  const allProfessions = useSelector((state) => state.user.allProfessions);
  const allSkillCategories = useSelector(
    (state) => state.user.allSkillCategories
  );
  const averageRating = useSelector((state) => state.job.averageRating) || {
    average: 49,
    max: 100,
    min: 18,
  };
  const selectedProfession = getProfessionCode(allProfessions, professionType);
  const profRate = averageRating[selectedProfession];
  const { title, text, route, params } = useSelector(
    (state) => state.message.message
  );

  useEffect(() => {
    if (!allProfessions || allProfessions?.length === 0) {
      dispatch(getAllProfessionals());
    }
    if (!allSkillCategories?.length) {
      dispatch(getAllSkillCategories());
    }
  }, []);

  useEffect(() => {
    let skills = 'skill-general';
    if (professionType === 'Dental Assistant') {
      skills = 'skill-da-general';
    } else if (professionType === 'Associate Dentist') {
      skills = 'experienced-procedures-oral-examinations';
    }
    setFormValues((prevState) => ({ ...prevState, skills }));
  }, [professionType]);

  const showContent = (type) => {
    setContent(type);
    if (type !== 'break') {
      setShowStartTime(true);
    } else {
      setModalOpen(true);
    }
  };

  const resultFromModal = (data) => {
    if (content === 'start_time') {
      setFormValues((prevState) => ({ ...prevState, offerStartTime: data }));
    } else if (content === 'end_time') {
      setFormValues((prevState) => ({ ...prevState, offerEndTime: data }));
    } else if (content === 'break') {
      setFormValues((prevState) => ({ ...prevState, lunch: data.time }));
      setFormValues((prevState) => ({ ...prevState, isLunchPaid: data.paid }));
    }
  };

  const renderJobField = (title, value, contentType, icon, disabled) => (
    <div
      className="row_container"
      style={{
        borderBottom: `1px solid ${Colors.neutral_100}`,
        width: '50%',
        cursor: disabled ? 'default' : 'pointer',
      }}
      onClick={() => !disabled && showContent(contentType)}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            alt="icon"
            src={icon}
            style={{ width: 24, height: 24, marginRight: 8 }}
          />
          <p className="date">{title}</p>
        </div>

        <span
          style={{
            fontFamily: 'Nunito',
            color: Colors.neutral_600,
            textTransform: 'capitalize',
          }}
        >
          {value}
        </span>
      </div>
      {!disabled && (
        <div>
          <img
            alt="arrow"
            src={arrowIcon}
            style={{ marginLeft: 30, marginRight: -30 }}
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {renderJobField(
          'Office:',
          user?.office_name,
          undefined,
          houseIcon,
          true
        )}

        {parentUser.childOffices.length > 0 &&
          user.isWorkerClassificationEnabled &&
          user.workerClassification &&
          renderJobField(
            <span
              style={{
                marginRight: 16,
              }}
            >
              Worker Type:
              <span
                style={{
                  marginLeft: '5px',
                  position: 'relative',
                  top: '5px',
                }}
              >
                <Tooltip content={workerClassificationTooltipDescription} />
              </span>
            </span>,
            user.workerClassification.classificationType === 'contractor'
              ? 'Contracted'
              : 'Employed',
            undefined,
            briefcaseIcon,
            true
          )}

        {job &&
          renderJobField(
            'Date:',
            moment(job?.localDate).format('ddd, MMM DD, YYYY'),
            undefined,
            calendarIcon,
            true
          )}

        {!job && <CalendarField />}

        {job &&
          renderJobField(
            'Professional:',
            !professionType ? 'Profession Type' : professionType,
            undefined,
            userIcon,
            true
          )}

        {!job && allProfessions?.length > 1 && (
          <div style={{ width: '50%' }}>
            <ProfessionPopup
              title="Professional:"
              value={!professionType ? 'Profession Type' : professionType}
              icon={userIcon}
              type="profession"
              submitProfession={(professionType) =>
                setFormValues((prevState) => ({ ...prevState, professionType }))
              }
            />
          </div>
        )}

        <div style={{ width: '50%' }}>
          <SkillsPopup
            title={
              professionType === 'Associate Dentist'
                ? 'Shift Procedures:'
                : 'Preferred Specialty:'
            }
            professionType={professionType}
            defaultSkills={skills}
            skillsSubmit={(selectedSkills) =>
              setFormValues((prevState) => ({
                ...prevState,
                skills: selectedSkills,
              }))
            }
            tooltip={
              professionType === 'Associate Dentist'
                ? 'Here you can list all the procedures that the Professional will be expected to complete. This makes it easier for both the temp and offices so both parties are on the same page in regards to what the Dentist will be specifically doing on the shift.'
                : null
            }
            isAssociateDentist={professionType === 'Associate Dentist'}
          />
        </div>

        {renderJobField(
          <span
            style={{
              marginRight: 16,
            }}
          >
            Arrival:
            <span
              style={{
                marginLeft: '5px',
                position: 'relative',
                top: '5px',
              }}
            >
              <Tooltip content={arrivalTooltipDescription} />
            </span>
          </span>,
          !offerStartTime ? (
            <span style={{ color: Colors.neutral_300 }}>Time</span>
          ) : (
            offerStartTime
          ),
          'start_time',
          startClockIcon
        )}

        {renderJobField(
          <span
            style={{
              marginRight: 16,
            }}
          >
            Departure:
            <span
              style={{
                marginLeft: '5px',
                position: 'relative',
                top: '5px',
              }}
            >
              <Tooltip content={departureTooltipDescription} />
            </span>
          </span>,
          !offerEndTime ? (
            <span style={{ color: Colors.neutral_300 }}>Time</span>
          ) : (
            offerEndTime
          ),
          'end_time',
          endClockIcon
        )}

        <div
          className="row_container"
          style={{
            borderBottom: `1px solid ${Colors.neutral_100}`,
            width: '50%',
            justifyContent: 'space-between',
          }}
          onClick={() => setShowRatePopup(true)}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img
                alt="icon"
                src={rateIcon}
                style={{ width: 24, height: 24, marginRight: 8 }}
              />
              <p className="date">Hourly Rate:</p>
            </div>
            <p
              className="date_value"
              style={{ textAlign: 'right', color: Colors.neutral_600 }}
            >
              {rate ? (
                `$ ${rate}`
              ) : (
                <span style={{ color: Colors.neutral_300 }}>$ ⏤.⏤</span>
              )}
            </p>
          </div>
          <div>
            <img
              alt="arrow"
              src={arrowIcon}
              style={{ marginLeft: 30, marginRight: -30 }}
            />
          </div>
        </div>

        <div
          className="row_container"
          style={{
            borderBottom: `1px solid ${Colors.neutral_100}`,
            width: '50%',
            justifyContent: 'space-between',
          }}
          onClick={() => showContent('break')}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img
                alt="icon"
                src={breakIcon}
                style={{ width: 24, height: 24, marginRight: 8 }}
              />
              <p className="date">Break:</p>
            </div>
            <p
              className="date_value"
              style={{ textAlign: 'right', color: Colors.neutral_600 }}
            >
              {lunch ? (
                `${lunch} min ${isLunchPaid ? '(Paid)' : '(Unpaid)'}`
              ) : (
                <span style={{ color: Colors.neutral_300 }}>Time</span>
              )}
            </p>
          </div>
          <div>
            <img
              alt="arrow"
              src={arrowIcon}
              style={{ marginLeft: 30, marginRight: -30 }}
            />
          </div>
        </div>

        <div
          style={{
            width: '50%',
            borderBottom: `1px solid ${Colors.neutral_100}`,
          }}
        >
          <div className="row_container" style={{ cursor: 'default' }}>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img
                  alt="icon"
                  src={autoFillIcon}
                  style={{
                    width: 24,
                    height: 24,
                    marginLeft: 4,
                    marginRight: 6,
                  }}
                />

                <p
                  className="date"
                  style={{
                    marginRight: 16,
                  }}
                >
                  Auto-Confirm:
                  <span
                    style={{
                      marginLeft: '5px',
                      position: 'relative',
                      top: '5px',
                    }}
                  >
                    <Tooltip content={autoFillTooltipDescription} />
                  </span>
                </p>
              </div>
            </div>

            <motion.div
              animate={
                getAutoFillInfo(professionType, skills).show ? 'show' : 'hide'
              }
              variants={opacityVariants}
            >
              <ReactSwitch
                checked={enableAutoFill}
                onChange={() =>
                  setFormValues((prevState) => ({
                    ...prevState,
                    enableAutoFill: !enableAutoFill,
                  }))
                }
                onColor={Colors.secondary_500}
                onHandleColor="#fff"
                handleDiameter={20}
                uncheckedIcon={false}
                checkedIcon={false}
                offColor={Colors.neutral_100}
                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                height={25}
                width={50}
                className="react-switch"
                id="material-switch"
              />
            </motion.div>
          </div>

          <motion.p
            key={getAutoFillInfo(professionType, skills).message}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="global_font"
            style={{
              color: Colors.neutral_500,
              fontStyle: 'italic',
              padding: '0px 40px',
              marginTop: 0,
              marginBottom: 25,
            }}
          >
            {getAutoFillInfo(professionType, skills).message}
          </motion.p>
        </div>
      </div>

      <MobileAppModal
        show={modalIsOpen}
        closeModal={() => setModalOpen(false)}
        height={973}
        content={content}
        result={resultFromModal}
      />

      {showStartTime ? (
        <ShowStartTime
          content={content}
          result={resultFromModal}
          closeModal={() => setShowStartTime(false)}
          height={973}
          defaultStartTime={offerStartTime}
          defaultEndTime={offerEndTime}
        />
      ) : null}

      {showRatePopup ? (
        <ModalRatePopup
          closeModal={() => setShowRatePopup(false)}
          submitRate={(offerRate) =>
            setFormValues((prevState) => ({ ...prevState, rate: offerRate }))
          }
          averagePrice={profRate}
          defaultValue={rate}
        />
      ) : (
        <div />
      )}
      {text ? (
        <CustomPortal
          title={title}
          text={text}
          route={route}
          params={params}
          submitPressed={() => hideMessage()}
        />
      ) : null}
    </>
  );
};
